/**
 * Which face each of the engine's cast members wears.
 *
 * ========================= WHAT THIS FILE IS FOR ===============================
 * `engine/cast.ts` invents 28 people — names, short names, and an `avatarId` that has
 * been `null` since it was written because the portraits did not exist yet. They exist
 * now: 26 keyed characters and 2 dealers, each with neutral / amused / stare, synced
 * into `public/art/avatars/` by `scripts/sync-art.mjs`.
 *
 * This is the mapping, and it lives in the WEB layer on purpose. Two reasons:
 *
 *   1. The overhaul's standing rule is that `app/engine/` is not touched, and
 *      `tests/cast.test.ts` asserts every cast member's `avatarId` is null — the seat
 *      must not carry a portrait id the engine cannot honour. Art is a client concern.
 *   2. `TableState` does not put the cast ID on the wire at all (see `game.ts`: the
 *      seat's identity is name, short name and a null avatarId, deliberately, so the
 *      archetype cannot be recovered from it). The full name IS on the wire and IS
 *      unique across the roster, so that is what a seat resolves by.
 *
 * ## The assignment stays uncorrelated with archetype
 *
 * `cast.ts` is emphatic about this and it survives here untouched: a cast member is
 * dealt to a seat by `castFor()` from an independent RNG stream, and the archetype is
 * dealt separately. This file maps a NAME to a FACE — a constant, session-invariant
 * pairing — so it adds no information about how anybody plays. Preacher Job looks like
 * a preacher in every session and still plays whatever he was dealt.
 *
 * Faces are matched to names for flavour only, and where a name says what somebody is
 * ("Preacher Job", "Doc Rennick") the face agrees with the name.
 * ===============================================================================
 */
import { CAST } from "@engine/cast";

/** An expression sheet. `stare` is the hard look, `amused` the smile. */
export type Expression = "neutral" | "amused" | "stare";

export interface RosterEntry {
  /** The cast id in `engine/cast.ts`. */
  castId: string;
  /** The slug `Poker assets/export/avatars/` files this character under. */
  slug: string;
  /** Which expressions are real art rather than a stand-in. */
  expressions: readonly Expression[];
}

/**
 * The two dealers are art, not players.
 *
 * `export/README.md` records the caution: the dealer sheets' head row is
 * front / front / PROFILE rather than smile / hard / flat, so `dealer-*-stare` is a
 * side profile and `-amused` and `-neutral` are two near-identical fronts. Until they
 * are re-rendered with the three expressions, a dealer has ONE usable face.
 */
const DEALER_EXPRESSIONS = ["neutral"] as const;
const FULL_EXPRESSIONS = ["neutral", "amused", "stare"] as const;

/** The dealer at a blackjack table. Not a seat; the room picks one of these. */
export const DEALERS = ["dealer-m", "dealer-f"] as const;

/**
 * Cast id -> character slug.
 *
 * ## The one place this is not one-to-one, and why
 *
 * There are 28 cast members and 28 slugs, but they do not split the same way: the cast
 * is 16 men and 12 women, and the art is 15 male-presenting slugs (14 characters plus
 * `dealer-m`) and 13 female-presenting (12 characters plus `dealer-f`). A strict
 * one-to-one therefore has to put one man on a woman's portrait — `dealer-f` is an
 * eye-patched woman in a long skirt, so that assignment would read as a bug rather
 * than as casting.
 *
 * So: all 26 characters are used once each, `dealer-m` is used by TWO cast members
 * (`agnew` and `bosley`), and `dealer-f` is reserved for the blackjack room's dealer,
 * where a dealer belongs. Nine-handed poker seats nine of these at a time, so the odds
 * of Thaddeus Agnew and Otis Bosley sharing a table are low and the collision is
 * cosmetic when it happens. Flagged for the planning session: the alternative is to
 * re-render one more male character, or to accept `bosley` on `dealer-f`.
 */
const ASSIGNMENT: readonly (readonly [castId: string, slug: string])[] = [
  // The men.
  ["creed",    "marshal-m"],     // Amos Creed
  ["mercer",   "banker-m"],      // Josiah Mercer
  ["tolliver", "prospector-m"],  // Ezra Tolliver
  ["bridger",  "trapper-m"],     // Silas Bridger
  ["deadeye",  "drifter-m"],     // Deadeye Hollis — the gunhand
  ["calloway", "rancher-m"],     // Nate Calloway
  ["stroud",   "blacksmith-m"],  // Abel Stroud
  ["preacher", "preacher-m"],    // Preacher Job — the name says it
  ["finch",    "shopkeeper-m"],  // Wilbur Finch
  ["doc",      "undertaker-m"],  // Doc Rennick
  ["bell",     "gambler-m"],     // Gideon Bell
  ["crow",     "cardsharp-m"],   // Jonah Crow
  ["holt",     "barkeep-m"],     // Rufus Holt
  ["dunmore",  "vaquero-m"],     // Cyrus Dunmore
  ["agnew",    "dealer-m"],      // Thaddeus Agnew
  ["bosley",   "dealer-m"],      // Otis Bosley — shares, see above
  // The women.
  ["halloran", "gambler-f"],     // Della Halloran
  ["vance",    "marshal-f"],     // Ruth Vance
  ["shaw",     "shopkeeper-f"],  // Minnie Shaw
  ["quinn",    "barkeep-f"],     // Orla Quinn
  ["pike",     "prospector-f"],  // Hattie Pike
  ["lamont",   "banker-f"],      // Cora Lamont
  ["ashby",    "drifter-f"],     // Nell Ashby
  ["marsh",    "undertaker-f"],  // Etta Marsh
  ["teague",   "preacher-f"],    // Verna Teague
  ["kestrel",  "rancher-f"],     // Sallie Kestrel
  ["whitlock", "blacksmith-f"],  // Pearl Whitlock
  ["river",    "vaquero-f"],     // Lucinda River
];

const isDealer = (slug: string): boolean => (DEALERS as readonly string[]).includes(slug);

export const ROSTER: readonly RosterEntry[] = ASSIGNMENT.map(([castId, slug]) => ({
  castId,
  slug,
  expressions: isDealer(slug) ? DEALER_EXPRESSIONS : FULL_EXPRESSIONS,
}));

const BY_CAST_ID: ReadonlyMap<string, RosterEntry> =
  new Map(ROSTER.map((r) => [r.castId, r]));

/**
 * Full name -> entry.
 *
 * Built from `CAST` rather than typed out a second time, so a name that changes in the
 * engine cannot leave a stale copy here. Short names are indexed too: the seat box on
 * a phone shows "Amos", not "Amos Creed", and the harness renders from short names.
 */
const BY_NAME = new Map<string, RosterEntry>();
for (const member of CAST) {
  const entry = BY_CAST_ID.get(member.id);
  if (!entry) continue;
  BY_NAME.set(member.name, entry);
  BY_NAME.set(member.short, entry);
}

/**
 * Every cast member has a face.
 *
 * Checked at module load rather than in a test, because the failure it prevents is a
 * seat rendering a broken image at a table somebody is playing at, and the cost of
 * checking is one pass over 28 strings at startup.
 */
const unmapped = CAST.filter((c) => !BY_CAST_ID.has(c.id)).map((c) => c.id);
if (unmapped.length) {
  throw new Error(`art/roster.ts has no face for: ${unmapped.join(", ")}`);
}

export function rosterFor(castId: string): RosterEntry | null {
  return BY_CAST_ID.get(castId) ?? null;
}

/** The entry for a seat, resolved from the only identity the wire carries: the name. */
export function rosterForName(name: string): RosterEntry | null {
  return BY_NAME.get(name) ?? null;
}

/**
 * The URL of one avatar, at one of the two synced tiers.
 *
 * 128 is what a seat loads — a 38–64px portrait at 2x DPR, and 3–5 KB of WebP. 512 is
 * the PNG tier, for the Ledger's archetype reveal and the profile, where a portrait is
 * the subject rather than a marker.
 *
 * An expression the character does not have falls back to `neutral` rather than
 * 404ing: the dealers have one real face, and asking a dealer to look amused should
 * quietly get the face that exists.
 */
export function avatarUrl(
  slug: string,
  expression: Expression = "neutral",
  size: 128 | 512 = 128,
): string {
  const entry = ROSTER.find((r) => r.slug === slug);
  const expr = entry && !entry.expressions.includes(expression) ? "neutral" : expression;
  const ext = size === 128 ? "webp" : "png";
  return `/art/avatars/${size}/${slug}-${expr}.${ext}`;
}

/**
 * The portrait for a seat, by the name the table shows.
 *
 * Returns null for a name that is not in the cast — the harness and the stub API both
 * invent seats, and a made-up name should get the silhouette rather than an exception.
 */
export function avatarUrlForName(
  name: string,
  expression: Expression = "neutral",
  size: 128 | 512 = 128,
): string | null {
  const entry = rosterForName(name);
  return entry ? avatarUrl(entry.slug, expression, size) : null;
}
